"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { useEventListener } from "@/helpers/useEventListener";
import {
  TruendoConsentData,
  TruendoCookieControlEvent,
} from "./TruendoCookieConsentInterfaces";
import { initialConsentData } from "./initialConsentData";
import { TruendoBannerReserve } from "./TruendoBannerReserve";

const TruendoCookieControlEventType = "TruendoCookieControl";

const TRUENDO_BANNER_SELECTORS = [
  "#truendo-root",
  "#truendo-cookie-banner",
  '[id*="truendo" i]',
  '[class*="truendo" i]',
  "#pc-banner",
  ".pc-banner",
  "#pcmp-container",
  '[id*="pcmp" i]',
  '[class*="pcmp" i]',
];

function isElementVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) {
    return false;
  }

  const style = window.getComputedStyle(el);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    Number(style.opacity) === 0
  ) {
    return false;
  }

  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function isTruendoBannerVisible(): boolean {
  for (const selector of TRUENDO_BANNER_SELECTORS) {
    for (const el of document.querySelectorAll(selector)) {
      if (isElementVisible(el)) {
        return true;
      }
    }
  }

  for (const iframe of document.querySelectorAll("iframe")) {
    if (iframe.src.includes("priv.center") && isElementVisible(iframe)) {
      return true;
    }
  }

  for (const el of document.querySelectorAll(
    '[role="dialog"], [aria-modal="true"]',
  )) {
    if (!isElementVisible(el) || !(el instanceof HTMLElement)) {
      continue;
    }

    const rect = el.getBoundingClientRect();
    if (rect.width > 200 && rect.height > 100) {
      return true;
    }
  }

  return false;
}

function hasStoredTruendoConsent(): boolean {
  try {
    if (/truendo|pc-consent|priv\.center/i.test(document.cookie)) {
      return true;
    }

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i) ?? "";
      if (/truendo|pc-consent|priv\.center/i.test(key)) {
        return true;
      }
    }
  } catch {
    return false;
  }

  return false;
}

interface TruendoConsentContextValue extends TruendoConsentData {
  truendoBlocked: boolean;
  truendoBannerDismissed: boolean;
}

const TruendoConsentContext = createContext<TruendoConsentContextValue>({
  ...initialConsentData,
  truendoBlocked: false,
  truendoBannerDismissed: false,
});

export const useConsentData = (): TruendoConsentContextValue =>
  useContext(TruendoConsentContext);

/**
 * Renders the truendo consent manager, and provides all child components
 *  access to consent data via context.
 */
export function TruendoConsentProvider({ children }: { children: ReactNode }) {
  const [consentData, setConsentData] =
    useState<TruendoConsentData>(initialConsentData);
  const [truendoBlocked, setTruendoBlocked] = useState(false);
  const [truendoBannerDismissed, setTruendoBannerDismissed] = useState(false);

  const dismissTruendoBanner = useCallback(() => {
    setTruendoBannerDismissed(true);
  }, []);

  const eventCallback = useCallback(
    (event: Event) => {
      const newConsentData = (event as TruendoCookieControlEvent).detail;

      setConsentData(newConsentData);
      dismissTruendoBanner();
    },
    [dismissTruendoBanner],
  );

  useEventListener(TruendoCookieControlEventType, eventCallback);

  useEffect(() => {
    if (truendoBlocked) {
      dismissTruendoBanner();
    }
  }, [truendoBlocked, dismissTruendoBanner]);

  useEffect(() => {
    if (truendoBannerDismissed) {
      return;
    }

    let sawBanner = false;

    const interval = window.setInterval(() => {
      const bannerVisible = isTruendoBannerVisible();
      if (bannerVisible) {
        sawBanner = true;
      }

      if (sawBanner && !bannerVisible) {
        dismissTruendoBanner();
        return;
      }

      if (
        window.Truendo &&
        hasStoredTruendoConsent() &&
        !bannerVisible &&
        !sawBanner
      ) {
        dismissTruendoBanner();
      }
    }, 200);

    return () => window.clearInterval(interval);
  }, [truendoBannerDismissed, dismissTruendoBanner]);

  // Detect if Truendo is blocked by ad blockers (like Brave)
  useEffect(() => {
    const checkTruendoLoaded = setTimeout(() => {
      if (!window.Truendo) {
        setTruendoBlocked(true);
        console.warn(
          "Truendo cookie consent manager is blocked. This is common with Brave browser or ad blockers.",
        );
      }
    }, 3000); // Wait 3 seconds for Truendo to load

    return () => clearTimeout(checkTruendoLoaded);
  }, []);

  const contextValue: TruendoConsentContextValue = {
    ...consentData,
    truendoBlocked,
    truendoBannerDismissed,
  };

  return (
    <TruendoConsentContext.Provider value={contextValue}>
      <TruendoBannerReserve />
      {children}
    </TruendoConsentContext.Provider>
  );
}
